import { Badge, HStack, Heading, Input, Stack, Text } from "@chakra-ui/react";
import * as React from "react";
import { LuSearch, LuTarget } from "react-icons/lu";
import { useParams, useSearchParams } from "react-router-dom";

import {
  GoogleAdsAdGroup,
  GoogleAdsCampaignRow,
  getGoogleAdsAdGroups,
  getGoogleAdsCampaign,
} from "../api/google-ads";
import { useBreadcrumbs } from "../components/breadcrumbs";
import DataTable from "../components/data-table";
import { EmptyState } from "../components/empty-state";
import IconCircle from "../components/icon-circle";
import { InputGroup } from "../components/input-group";
import { MetricCard, MetricGroup } from "../components/metric-card";
import Page from "../components/page";
import PageBody from "../components/page-body";
import {
  SegmentedControl,
  SegmentedControlItem,
} from "../components/segmented-control";
import { StatusPill } from "../components/status-pill";
import { useAppSelector } from "../hooks";
import { useGoogleAdsAccountName } from "../hooks/google-ads-account-name";
import { useGoogleAdsLoginCustomerId } from "../hooks/google-ads-login-customer-id";
import { useLoading } from "../hooks/loading";
import { useToaster } from "../hooks/toaster";
import { useWorkspaceURL } from "../hooks/workspace-url";
import { formatStatus } from "../lib/format";
import {
  CampaignTab,
  KEYWORD_AGENT_STATUS_LABELS,
  KEYWORD_AGENT_STATUS_TONES,
  getKeywordAgentDisplayStatus,
  isPausedInGoogle,
  matchesAdGroupTab,
} from "../lib/keyword-agent-status";

const AD_GROUP_TABS: SegmentedControlItem[] = [
  { value: "all", label: "All ad groups" },
  { value: "active", label: "Active" },
  { value: "needs-review", label: "Needs review" },
];

const DEFAULT_TAB: CampaignTab = "all";

const tabValue = (value: string | null): CampaignTab =>
  AD_GROUP_TABS.some((tab) => tab.value === value)
    ? (value as CampaignTab)
    : DEFAULT_TAB;

// An ad group with no agent has nothing to show in these columns, and a
// zero would read as "the agent found nothing".
const orDash = (adGroup: GoogleAdsAdGroup, value: number) =>
  adGroup.agent && value > 0 ? value.toLocaleString() : "—";

export default function GoogleAdGroupsPage() {
  const { customerId, campaignId } = useParams();
  const { loginCustomerId, withLoginCustomerId } =
    useGoogleAdsLoginCustomerId();
  const workspaceSlug = useAppSelector((state) => state.workspace.slug);
  const workspaceURL = useWorkspaceURL();
  const [searchParams, setSearchParams] = useSearchParams();
  const withToaster = useToaster();

  const [adGroups, setAdGroups] = React.useState<GoogleAdsAdGroup[]>([]);
  // Read on its own rather than off the first row: a campaign with no ad
  // groups still has to name itself in the header and the breadcrumb.
  const [campaign, setCampaign] = React.useState<GoogleAdsCampaignRow | null>(
    null,
  );
  const [isLoading, withLoading] = useLoading();
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);

  const numericCampaignId = parseInt(campaignId || "0");
  const accountName = useGoogleAdsAccountName(workspaceSlug, customerId);

  useBreadcrumbs([
    { label: "Dashboard", to: workspaceURL("") },
    { label: "Google", to: workspaceURL("google-ads-accounts") },
    {
      label: accountName,
      to: withLoginCustomerId(
        workspaceURL(`google-ads-accounts/${customerId}/campaigns`),
      ),
    },
    { label: campaign?.name ?? "…" },
  ]);

  const searchQuery = searchParams.get("search") || "";
  const tab = tabValue(searchParams.get("tab"));

  const loadAdGroups = React.useCallback(async () => {
    if (!workspaceSlug || !customerId || !campaignId) return;
    await withLoading(async () => {
      await withToaster(async () => {
        const [campaignData, adGroupData] = await Promise.all([
          getGoogleAdsCampaign(
            workspaceSlug,
            customerId,
            loginCustomerId,
            numericCampaignId,
          ),
          getGoogleAdsAdGroups(
            workspaceSlug,
            customerId,
            loginCustomerId,
            numericCampaignId,
          ),
        ]);
        setCampaign(campaignData);
        setAdGroups(adGroupData);
        setHasLoadedOnce(true);
      });
    });
  }, [
    campaignId,
    customerId,
    loginCustomerId,
    numericCampaignId,
    withLoading,
    withToaster,
    workspaceSlug,
  ]);

  React.useEffect(() => {
    loadAdGroups();
  }, [loadAdGroups]);

  const setParam = (key: string, value: string | null) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
      return params;
    });
  };

  const agentURL = (adGroupId: number) =>
    withLoginCustomerId(
      workspaceURL(
        `google-ads-accounts/${customerId}/campaigns/${campaignId}/ad-groups/${adGroupId}`,
      ),
    );

  const filtered = React.useMemo(
    () =>
      adGroups.filter((adGroup) => {
        if (!matchesAdGroupTab(adGroup, tab)) {
          return false;
        }
        return (
          !searchQuery ||
          adGroup.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }),
    [adGroups, searchQuery, tab],
  );

  // The campaign row already carries the sum across its ad groups, so the
  // band reads that rather than adding the rows up a second way.
  const totals = campaign?.stats;

  return (
    <Page loading={isLoading && !hasLoadedOnce}>
      <Stack flex="1" gap="6">
        <Stack gap="0.5">
          <Heading fontSize={"22px" as any} fontWeight="medium">
            {campaign?.name ?? "…"}
          </Heading>
          <Text color="fg.muted" fontSize="sm">
            {campaign
              ? [
                  formatStatus(campaign["advertising-channel-type"]),
                  formatStatus(campaign.status),
                ].join(" · ")
              : ""}
          </Text>
        </Stack>

        <PageBody>
          <MetricGroup>
            <MetricCard
              label="Negative Keywords Applied"
              value={(totals?.excluded ?? 0).toLocaleString()}
            />
            <MetricCard
              label="Pending review"
              value={(totals?.["pending-review"] ?? 0).toLocaleString()}
            />
            <MetricCard
              label="Search terms analyzed"
              value={(totals?.analyzed ?? 0).toLocaleString()}
            />
          </MetricGroup>

          <Stack gap="4" align="flex-start">
            <InputGroup
              startElement={<LuSearch size="16" />}
              width={"324px" as any}
              maxWidth="full"
            >
              <Input
                placeholder="Search for an ad group"
                value={searchQuery}
                onChange={(e) => setParam("search", e.target.value || null)}
              />
            </InputGroup>
            <SegmentedControl
              items={AD_GROUP_TABS}
              value={tab}
              onValueChange={(value) =>
                setParam("tab", value === DEFAULT_TAB ? null : value)
              }
            />
          </Stack>

          {filtered.length === 0 && !isLoading ? (
            <HStack flex="1" alignItems="center" paddingY="8">
              <EmptyState
                icon={<IconCircle Icon={LuTarget} size="md" variant="flat" />}
                title="No ad groups match this view"
                description={
                  adGroups.length === 0
                    ? "No ad groups found for this campaign."
                    : "Try a different search or filter."
                }
              />
            </HStack>
          ) : (
            <DataTable
              data={filtered}
              skeleton={isLoading && hasLoadedOnce ? 3 : undefined}
              template="minmax(240px,1fr) 132px 148px 120px"
              minTableWidth="740px"
              pagination
              rowHref={(adGroup) => agentURL(adGroup.id)}
              columns={[
                {
                  name: "Ad group",
                  cell: ({ value: adGroup }) => (
                    <HStack gap="1.5" minWidth="0">
                      <Text fontWeight="medium" truncate title={adGroup.name}>
                        {adGroup.name}
                      </Text>
                      {isPausedInGoogle(adGroup) && (
                        <Badge flexShrink="0">Paused</Badge>
                      )}
                    </HStack>
                  ),
                },
                {
                  name: "Agent",
                  cell: ({ value: adGroup }) => {
                    const status = getKeywordAgentDisplayStatus(adGroup);
                    return (
                      <StatusPill tone={KEYWORD_AGENT_STATUS_TONES[status]}>
                        {KEYWORD_AGENT_STATUS_LABELS[status]}
                      </StatusPill>
                    );
                  },
                },
                {
                  name: "Pending review",
                  align: "end",
                  cell: ({ value: adGroup }) => (
                    <Text>
                      {orDash(adGroup, adGroup.stats["pending-review"])}
                    </Text>
                  ),
                },
                {
                  name: "Excluded",
                  align: "end",
                  cell: ({ value: adGroup }) => (
                    <Text>{orDash(adGroup, adGroup.stats.excluded)}</Text>
                  ),
                },
              ]}
            />
          )}
        </PageBody>
      </Stack>
    </Page>
  );
}
